import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import { AppProvider, useApp } from './context/AppContext';
import Navbar from './components/Navbar';
import Onboarding from './components/Onboarding';
import AuthScreen from './components/AuthScreen';
import OAuthCallback from './components/OAuthCallback';
import DeckBrowser from './components/DeckBrowser';
import DeckView from './components/DeckView';
import DeckForm from './components/DeckForm';
import CardEditor from './components/CardEditor';
import StudyView from './components/StudyView';
import StatsView from './components/StatsView';
import GcseLibrary from './components/GcseLibrary';
import AiDeckGenerator from './components/AiDeckGenerator';
import Profile from './components/Profile';
import PricingModal from './components/PricingModal';
import InAppNudge from './components/InAppNudge';
import PublicRoute from './routes/PublicRoute';
import usePayment from './hooks/usePayment';
import './App.css';

function AppContent() {
  const { hasOnboarded, loading } = useApp();
  const { loading: authLoading } = useAuth();
  const subscription = useSubscription();
  // Bind CdvPurchase listener exactly once for the app's lifetime.
  // The hook uses subscriptionRef under the hood so it can read the latest
  // refreshSubscription without re-binding on render.
  usePayment(subscription);


  if (loading || authLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  // /auth/callback must be accessible regardless of onboarding state —
  // it handles the OAuth redirect back from Cognito after Apple Sign In.
  const pricingOverlay = subscription.pricingTrigger && (
    <PricingModal
      trigger={subscription.pricingTrigger}
      onClose={subscription.dismissPricing}
      onPurchaseComplete={() => subscription.refreshSubscription()}
    />
  );

  if (!hasOnboarded) {
    return (
      <>
        <Routes>
          <Route path="/auth/callback" element={<OAuthCallback />} />
          {/* GCSE library is browsable BEFORE onboarding — it's the landing
              pages' destination ("Try it in your browser"). Adding a deck
              there completes onboarding and drops straight into studying. */}
          <Route path="/library" element={<GcseLibrary />} />
          <Route path="/gcse" element={<GcseLibrary />} />
          <Route path="*" element={<Onboarding />} />
        </Routes>
        {pricingOverlay}
      </>
    );
  }

  return (
    <div className="app">
      <Navbar />
      <main className="main-content">
        <InAppNudge nudge={subscription.inAppNudge} />
        <Routes>
          <Route path="/" element={<DeckBrowser />} />
          <Route path="/deck/:deckId" element={<DeckView />} />
          <Route path="/deck/:deckId/add" element={<CardEditor />} />
          <Route path="/deck/:deckId/edit" element={<DeckForm />} />
          <Route path="/deck/:deckId/edit/:cardId" element={<CardEditor />} />
          <Route path="/decks/new" element={<DeckForm />} />
          <Route path="/decks/generate" element={<AiDeckGenerator />} />
          <Route path="/study/:deckId" element={<StudyView />} />
          <Route path="/stats" element={<StatsView />} />
          <Route path="/library" element={<GcseLibrary />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/auth/callback" element={<OAuthCallback />} />
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<AuthScreen initialMode="login" />} />
            <Route path="/signup" element={<AuthScreen initialMode="signup" />} />
          </Route>
        </Routes>
      </main>
      {pricingOverlay}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SubscriptionProvider>
          <AppProvider>
            <AppContent />
            <Toaster
              position="top-center"
              containerStyle={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
              toastOptions={{
                duration: 3500,
                style: {
                  borderRadius: '14px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  maxWidth: '92vw',
                  background: 'var(--toast-bg)',
                  color: 'var(--toast-fg)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
                  border: '1px solid var(--toast-border)',
                },
                success: {
                  iconTheme: { primary: 'var(--app-accent)', secondary: '#fff' },
                  duration: 2500,
                },
                error: {
                  iconTheme: { primary: '#ef4444', secondary: '#fff' },
                  duration: 4500,
                },
              }}
            />
          </AppProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
